import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import React from 'react';
import { UserDto } from 'src/api/FiretrolApiClient';
import {
  useArchiveUser,
  useUnarchiveUser,
} from 'src/api/queries/organization/useUserMutations';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { DangerButton } from 'src/design/atoms/DangerButton';
import { Modal } from 'src/design/molecules/Modal';
import { useConfirmableAction } from 'src/design/molecules/useConfirmableAction';
import { getErrorMessage, showErrorToast, showSuccessToast } from 'src/utils';

interface IArchiveUserButtonProps {
  user: UserDto;
}

export const ArchiveUserButton: React.FC<IArchiveUserButtonProps> = ({
  user,
}) => {
  const archiveUser = useArchiveUser();
  const unarchiveUser = useUnarchiveUser();
  const name = `${user.firstName} ${user.lastName}`;
  const archived = user.isArchived;
  // The cached user flips as soon as the mutation succeeds, but the modal stays
  // open until the follow-up refetch settles, so it must keep showing the
  // action chosen when it opened.
  const [unarchiving, setUnarchiving] = React.useState(archived);

  const { opened, loading, requestOrRun, confirm, close } =
    useConfirmableAction(async () => {
      try {
        if (unarchiving) {
          await unarchiveUser.mutateAsync(user.id);
          showSuccessToast(`${name} has been unarchived.`);
        } else {
          await archiveUser.mutateAsync(user.id);
          showSuccessToast(`${name} has been archived.`);
        }
      } catch (err) {
        showErrorToast(getErrorMessage(err));
        throw err;
      }
    }, true);

  return (
    <>
      <BasicButton
        size="sm"
        color={archived ? 'blue' : 'red'}
        variant={archived ? 'filled' : 'outline'}
        onClick={() => {
          setUnarchiving(archived);
          requestOrRun();
        }}
      >
        {archived ? 'Unarchive User' : 'Archive User'}
      </BasicButton>
      <Modal
        size="md"
        title={unarchiving ? `Unarchive ${name}?` : `Archive ${name}?`}
        opened={opened}
        onClose={close}
        actions={[
          unarchiving ? (
            <BasicButton
              key="unarchive"
              loading={loading}
              onClick={() => confirm()}
            >
              Unarchive User
            </BasicButton>
          ) : (
            <DangerButton
              key="archive"
              loading={loading}
              onClick={() => confirm()}
            >
              Archive User
            </DangerButton>
          ),
        ]}
      >
        {unarchiving ? (
          <Text>
            {name} will appear in pickers and lists again and will be able to
            sign in.
          </Text>
        ) : (
          <Stack gap="xs">
            <Text>
              {name} will be hidden from pickers and lists and will not be able
              to sign in. Historical records keep showing their name.
            </Text>
            <Text>
              Inviting the same email address later restores this user.
            </Text>
          </Stack>
        )}
      </Modal>
    </>
  );
};
